/* PILSNERISH OS — core runtime (window.PZ)
 * Contract (SPEC §3): PZ.reduced, PZ.lenis, PZ.scramble(el, opts) -> Promise,
 *   scramble opts: { accent, text, duration, interval, ratio, accentChance, mode: 'rgb', palette: [[color, weight], ...] }
 * PZ.onSection(name, init), PZ.inView(el, cb, opts), PZ.cart (pz-cart.js).
 */
(function () {
  'use strict';
  var w = window, d = document;
  var PZ = (w.PZ = w.PZ || {});
  var mqReduced = w.matchMedia('(prefers-reduced-motion: reduce)');
  var cfg = w.PZ_CFG || {};

  PZ.reduced = mqReduced.matches;
  PZ.designMode = !!(w.Shopify && w.Shopify.designMode);
  PZ.hover = w.matchMedia('(hover: hover) and (pointer: fine)').matches;
  PZ.touch = !PZ.hover;

  /* ---------- GSAP + Lenis ---------- */
  if (w.gsap) {
    var plugins = [w.ScrollTrigger, w.SplitText, w.Flip].filter(Boolean);
    if (plugins.length) w.gsap.registerPlugin.apply(w.gsap, plugins);
  }

  PZ.lenis = null;
  if (!PZ.reduced && w.Lenis && !PZ.designMode && cfg.smoothScroll !== false) {
    try {
      var lenis = new w.Lenis({ lerp: 0.1, autoRaf: !w.gsap, anchors: true, stopInertiaOnNavigate: true });
      if (w.gsap) {
        if (w.ScrollTrigger) lenis.on('scroll', w.ScrollTrigger.update);
        /* R8 CPU: Lenis only rides the GSAP ticker while it has something to do (scroll input, a programmatic scroll,
           an easing in progress). ~20 idle frames later it detaches, so with nothing else animating GSAP's ticker
           auto-sleeps and no rAF loop runs at the display rate (144-360 Hz on some screens) for a page at rest.
           Its clock is re-based on wake so the first frame after a pause does not jump. */
        var lenisOn = false, lenisIdle = 0, lenisLast = 0;
        var lenisTick = function (t) {
          var ms = t * 1000;
          if (!lenisLast || ms - lenisLast > 100) lenis.time = ms - 16.7;
          lenisLast = ms;
          lenis.raf(ms);
          if (lenis.isScrolling || (lenis.animate && lenis.animate.isRunning)) { lenisIdle = 0; return; }
          if (++lenisIdle > 20) { w.gsap.ticker.remove(lenisTick); lenisOn = false; lenisLast = 0; }
        };
        var wakeLenis = function () { lenisIdle = 0; if (!lenisOn) { lenisOn = true; w.gsap.ticker.add(lenisTick); } };
        ['wheel', 'touchstart', 'touchmove', 'touchend', 'keydown', 'pointerdown', 'scroll'].forEach(function (ev) { w.addEventListener(ev, wakeLenis, { capture: true, passive: true }); });
        var lenisScrollTo = lenis.scrollTo.bind(lenis);
        lenis.scrollTo = function () { wakeLenis(); return lenisScrollTo.apply(null, arguments); };
        PZ.wakeScroll = wakeLenis;
        wakeLenis();
        w.gsap.ticker.lagSmoothing(0);
      }
      PZ.lenis = lenis;
    } catch (e) { console.warn('[PZ] lenis', e); }
  }

  PZ.scrollTo = function (target, opts) {
    opts = opts || {};
    var offset = opts.offset != null ? opts.offset : -(PZ.headerOffset() + 8);
    if (PZ.lenis) { PZ.lenis.scrollTo(target, { offset: offset, duration: opts.duration || 0.9, immediate: !!opts.immediate }); return; }
    var el = typeof target === 'string' ? d.querySelector(target) : target;
    var y = typeof target === 'number' ? target : (el ? el.getBoundingClientRect().top + w.scrollY + offset : 0);
    w.scrollTo({ top: y, behavior: PZ.reduced || opts.immediate ? 'auto' : 'smooth' });
  };
  PZ.lock = function (on) {
    d.documentElement.classList.toggle('pz-locked', !!on);
    if (PZ.lenis) { on ? PZ.lenis.stop() : PZ.lenis.start(); }
  };
  PZ.headerOffset = function () {
    var v = getComputedStyle(d.documentElement).getPropertyValue('--header-vis');
    return parseFloat(v) || 0;
  };

  /* ---------- scramble ---------- */
  var CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#$%&@▓░';
  var runs = new WeakMap();
  var segmenter = null;
  try { if (w.Intl && Intl.Segmenter) segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' }); } catch (e) {}
  function graphemes(str) {
    if (segmenter) { var out = []; for (var s of segmenter.segment(str)) out.push(s.segment); return out; }
    return Array.from(str);
  }
  function rnd() { return CHARS.charAt((Math.random() * CHARS.length) | 0); }
  /* weighted glitch palette: [color, weight]. R4: Matrix / VHS / green DOS terminal only: phosphor green,
     dim phosphor, greys and the occasional red. No yellow, blue, cyan or magenta anywhere. */
  var RGB_PALETTE = [['#33FF66', 5], ['#1F8F3A', 3], ['#7B8382', 2], ['#A9B0AD', 1], ['#ED1B24', 0.8]];
  PZ.RGB_PALETTE = RGB_PALETTE;
  function pickColor(pal) {
    var total = 0, i;
    for (i = 0; i < pal.length; i++) total += pal[i][1];
    var r = Math.random() * total;
    for (i = 0; i < pal.length; i++) { r -= pal[i][1]; if (r <= 0) return pal[i][0]; }
    return pal[0][0];
  }

  PZ.scramble = function (el, opts) {
    opts = opts || {};
    if (!el) return Promise.resolve();
    var prev = runs.get(el);
    if (prev) prev.cancel();
    if (PZ.reduced || cfg.scramble === false) {
      if (opts.text != null) el.textContent = opts.text;
      return Promise.resolve();
    }
    var original = Array.prototype.slice.call(el.childNodes);
    var finalText = opts.text != null ? String(opts.text) : el.textContent;
    var finalNodes = opts.text != null ? [d.createTextNode(finalText)] : original;
    var glyphs = graphemes(finalText);
    var ratio = opts.ratio != null ? opts.ratio : 0.6;
    var accentChance = opts.accentChance != null ? opts.accentChance : 0.15;
    var accent = opts.accent || 'var(--red)';
    var duration = opts.duration || 320;
    var interval = opts.interval || 60;
    /* mode 'rgb' = VHS glitch: every scrambled glyph takes a weighted palette color (mostly phosphor green),
       plus a jittering ghost: phosphor green one way, grey (sometimes red) the other; settles back to ink. */
    var rgb = opts.mode === 'rgb';
    var palette = opts.palette || RGB_PALETTE;
    var prevShadow = rgb ? el.style.textShadow : null;
    if (rgb) accentChance = opts.accentChance != null ? opts.accentChance : 1;
    var idx = [];
    glyphs.forEach(function (g, i) { if (/\S/.test(g) && Math.random() < ratio) idx.push(i); });
    if (!idx.length) {
      var nonSpace = glyphs.map(function (g, i) { return /\S/.test(g) ? i : -1; }).filter(function (i) { return i > -1; });
      if (nonSpace.length) idx.push(nonSpace[(Math.random() * nonSpace.length) | 0]);
    }
    var pick = {}; idx.forEach(function (i) { pick[i] = true; });

    return new Promise(function (resolve) {
      var raf = 0, t0 = performance.now(), last = -Infinity, done = false;
      function frame() {
        var frag = d.createDocumentFragment(), buf = '';
        for (var i = 0; i < glyphs.length; i++) {
          if (!pick[i]) { buf += glyphs[i]; continue; }
          var ch = rnd();
          if (Math.random() < accentChance) {
            if (buf) { frag.appendChild(d.createTextNode(buf)); buf = ''; }
            var sp = d.createElement('span');
            sp.style.color = rgb ? pickColor(palette) : accent;
            sp.textContent = ch;
            frag.appendChild(sp);
          } else buf += ch;
        }
        if (buf) frag.appendChild(d.createTextNode(buf));
        el.replaceChildren(frag);
        if (rgb) {
          var dx = (1 + Math.random()).toFixed(1), dy = (Math.random() * 1.2 - .6).toFixed(1);
          var ghost = Math.random() < 0.25 ? 'rgba(237,27,36,.6)' : 'rgba(123,131,130,.6)';
          el.style.textShadow = '-' + dx + 'px ' + dy + 'px 0 rgba(51,255,102,.6), ' + dx + 'px ' + (-dy) + 'px 0 ' + ghost;
        }
      }
      function finish() {
        if (done) return;
        done = true;
        cancelAnimationFrame(raf); clearTimeout(gapT);
        el.replaceChildren.apply(el, finalNodes);
        if (rgb) el.style.textShadow = prevShadow || '';
        runs.delete(el);
        resolve();
      }
      /* R8 CPU: one frame request per glyph step (every `interval` ms, 60 by default) instead of every display frame */
      var gapT = 0;
      function tick(now) {
        raf = 0;
        var t = now - t0;
        if (t >= duration) return finish();
        if (t - last >= interval) { last = t; frame(); }
        gapT = setTimeout(function () { if (!done) raf = requestAnimationFrame(tick); }, Math.max(0, interval - (performance.now() - t0 - last) - 4));
      }
      runs.set(el, { cancel: function () { if (!done) { done = true; cancelAnimationFrame(raf); el.replaceChildren.apply(el, original); if (rgb) el.style.textShadow = prevShadow || ''; runs.delete(el); resolve(); } } });
      raf = requestAnimationFrame(tick);
    });
  };
  /* R15: stop a running scramble and put its text back, so a caller can set a plain label (busy buttons) */
  PZ.unscramble = function (el) { var r = el && runs.get(el); if (r) r.cancel(); };

  /* ---------- section lifecycle ---------- */
  var registry = {};
  var destroyers = new Map();
  var ready = false;
  function initEl(el) {
    if (destroyers.has(el)) return;
    var name = el.getAttribute('data-pz-section');
    var init = registry[name];
    if (!init) return;
    try {
      var destroy = init(el);
      destroyers.set(el, typeof destroy === 'function' ? destroy : null);
    } catch (e) { console.error('[PZ] section "' + name + '" init failed', e); }
  }
  function scan(root, name) {
    var sel = name ? '[data-pz-section="' + name + '"]' : '[data-pz-section]';
    var list = Array.prototype.slice.call(root.querySelectorAll(sel));
    if (root.matches && root.matches(sel)) list.unshift(root);
    list.forEach(initEl);
  }
  function teardown(root) {
    destroyers.forEach(function (fn, el) {
      if (root === el || root.contains(el)) {
        if (fn) { try { fn(); } catch (e) { console.error('[PZ] destroy', e); } }
        destroyers.delete(el);
      }
    });
  }
  PZ.onSection = function (name, init) {
    registry[name] = init;
    if (ready) scan(d, name);
  };
  /* R8 CPU: every section off screen (or the tab hidden) gets .pz-off; CSS pauses its infinite animations
     (pz-theme.css) and section scripts can read PZ.isOff(el) */
  var offIO = 'IntersectionObserver' in w ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { e.target.classList.toggle('pz-off', !e.isIntersecting); });
  }, { rootMargin: '100px 0px' }) : null;
  function watchOff(root) {
    if (!offIO) return;
    [].forEach.call((root || d).querySelectorAll('[data-pz-section]'), function (el) { offIO.observe(el); });
  }
  PZ.isOff = function (el) { return !!(el && el.classList.contains('pz-off')); };
  /* R8 CPU: after 45 s without any input the page is idle (html.pz-idle, PZ.idle, 'pz:idle' event): infinite CSS
     animations in sections pause and decorative loops (marquee, collab sky) hold still until the next input. Content
     keeps going (product videos, the pins stage rotation). */
  PZ.idle = false;
  var idleT = 0;
  function setIdle(on) {
    if (PZ.idle === on) return;
    PZ.idle = on;
    d.documentElement.classList.toggle('pz-idle', on);
    d.dispatchEvent(new CustomEvent('pz:idle', { detail: on }));
  }
  var pokeAt = 0;
  function poke() {
    var now = Date.now();
    if (PZ.idle) setIdle(false);
    else if (now - pokeAt < 1000) return;   /* move/scroll events arrive at the display rate: re-arm at most once a second */
    pokeAt = now;
    clearTimeout(idleT);
    idleT = setTimeout(function () { setIdle(true); }, 45000);
  }
  /* R9 live phones: every kind of touch counts as input (a finger resting or dragging on a phone sends touchmove and
     pointermove without a new touchstart), plus native scroll (momentum), taps, rotation and coming back to the tab */
  ['pointermove', 'pointerdown', 'pointerup', 'wheel', 'scroll', 'keydown', 'touchstart', 'touchmove', 'touchend', 'click'].forEach(function (ev) { w.addEventListener(ev, poke, { capture: true, passive: true }); });
  /* R9: window-only (a captured 'resize' also catches every <video> size change, which would keep the page awake) */
  ['resize', 'orientationchange', 'focus'].forEach(function (ev) { w.addEventListener(ev, function (e) { if (e.target === w) poke(); }, { passive: true }); });
  d.addEventListener('visibilitychange', function () { if (!d.hidden) poke(); });
  poke();
  /* R8 CPU: SUBSCRIBE chip LED blink (bright 1.12 s, dim 0.48 s) as a class toggle, off while idle, hidden or reduced motion */
  var ledT = 0;
  function led(dim) {
    ledT = 0;
    var on = dim && !PZ.idle && !d.hidden && !PZ.reduced;
    d.documentElement.classList.toggle('pz-led-dim', on);
    if (PZ.idle || d.hidden || PZ.reduced) return;
    ledT = setTimeout(function () { led(!on); }, on ? 480 : 1120);
  }
  function ledWake() { if (!ledT) led(false); }
  d.addEventListener('pz:idle', function (e) { if (!e.detail) ledWake(); });
  d.addEventListener('visibilitychange', function () { if (!d.hidden) ledWake(); });
  led(false);
  function boot() {
    if (ready) return;
    ready = true;
    watchOff(d);
    scan(d);
    d.dispatchEvent(new CustomEvent('pz:ready'));
  }
  /* deferred scripts run while readyState is 'interactive' (before DOMContentLoaded), so wait for DCL
     to let every section script register first; 'load' is a fallback for late injection. */
  if (d.readyState === 'complete') setTimeout(boot, 0);
  else { d.addEventListener('DOMContentLoaded', boot); w.addEventListener('load', boot); }
  d.addEventListener('shopify:section:load', function (e) {
    watchOff(e.target);
    scan(e.target);
    if (w.ScrollTrigger) w.ScrollTrigger.refresh();
  });
  d.addEventListener('shopify:section:unload', function (e) {
    teardown(e.target);
    if (w.ScrollTrigger) setTimeout(function () { w.ScrollTrigger.refresh(); }, 0);
  });
  /* launch gate perf: ScrollTrigger already refreshes itself on load, so the extra refresh here doubled the biggest
     long task on the home page (~700 ms on a throttled phone). Refresh only if ScrollTrigger did not. */
  var stRefreshAt = -1;
  d.addEventListener('pz:ready', function () {
    if (w.ScrollTrigger) w.ScrollTrigger.addEventListener('refresh', function () { stRefreshAt = performance.now(); });
  });
  w.addEventListener('load', function () {
    if (!w.ScrollTrigger) return;
    var t0 = performance.now();
    setTimeout(function () { if (stRefreshAt < t0 - 50) w.ScrollTrigger.refresh(); }, 0);
  });

  /* ---------- video intro skips ----------
     Some product videos open with a splash (e.g. a VHS camera intro). For any <video> whose currentSrc
     contains an id from the "Video intro skips" theme setting (one id=seconds per line), seek past the
     intro whenever currentTime < offset - 0.1 (first play, loop restarts). Section-agnostic: document-level
     capture listeners (media events don't bubble) plus a play() hook for videos that never join the DOM
     (e.g. WebGL textures). */
  var SKIPS = [];
  String(cfg.videoSkips || '').split(/[\r\n,]+/).forEach(function (line) {
    var m = line.trim().match(/^([A-Za-z0-9_-]+)\s*=\s*([\d.]+)$/);
    if (m) SKIPS.push({ id: m[1], t: parseFloat(m[2]) });
  });
  PZ.videoSkips = SKIPS;
  function skipFor(v) {
    var src = v.currentSrc || v.src || '';
    if (!src) { var so = v.querySelector && v.querySelector('source'); if (so) src = so.src; }
    for (var i = 0; i < SKIPS.length; i++) if (src.indexOf(SKIPS[i].id) > -1) return SKIPS[i].t;
    return 0;
  }
  /* R2 hardening:
     - a native loop jumps back to 0 (a flash of the intro before the seek lands), so skip-listed looping
       videos loop by hand: loop is switched off and 'ended' seeks straight to the offset and plays;
     - a seek only goes out when the offset is inside v.seekable and no seek is pending. Before, a resource
       that was not seekable yet clamped every seek back to 0 and the seeked -> seek -> seeked loop froze the
       video on its poster (the 450 North card "not playing");
     - a started video that is still inside its intro (seek not possible yet) plays hidden with its poster
       (the post-intro frame) painted on its parent, and shows once it passes the offset.
     PZ.videoInIntro(v) tells WebGL users not to sample such a frame. */
  function seekableAt(v, t) {
    try { var s = v.seekable; for (var i = 0; i < s.length; i++) if (s.start(i) <= t && s.end(i) > t) return true; } catch (e) {}
    return false;
  }
  function setIntroHidden(v, on) {
    if (!!v.__pzHid === on) return;
    v.__pzHid = on;
    v.style.transition = on ? 'none' : '';
    v.style.opacity = on ? '0' : '';
    var p = v.parentNode;
    if (p && p.style && v.poster) {
      p.style.backgroundImage = on ? 'url("' + v.poster + '")' : '';
      p.style.backgroundSize = on ? 'cover' : '';
      p.style.backgroundPosition = on ? '50% 50%' : '';
    }
  }
  function applySkip(v, type) {
    if (!v || v.tagName !== 'VIDEO' || !SKIPS.length) return;
    var off = skipFor(v);
    if (!off) { if (v.__pzHid) setIntroHidden(v, false); return; }
    if (v.loop) { v.loop = false; v.__pzLoop = true; }
    if (type === 'play' || type === 'seeking') v.__pzGo = true;
    if (type === 'ended') {
      if (!v.__pzLoop) return;
      try { v.currentTime = seekableAt(v, off) ? off : 0; } catch (e) {}
      var pr = v.play(); if (pr && pr.catch) pr.catch(function () {});
      return;
    }
    if (v.readyState < 1) return;
    if (isFinite(v.duration) && v.duration <= off + 0.2) return;
    if (v.currentTime < off - 0.1 && !v.seeking && seekableAt(v, off)) { try { v.currentTime = off; } catch (e) {} }
    setIntroHidden(v, !!v.__pzGo && v.currentTime < off - 0.1);
  }
  PZ.applyVideoSkip = applySkip;
  PZ.videoInIntro = function (v) { return !!(v && v.__pzHid); };
  if (SKIPS.length) {
    ['loadstart', 'loadedmetadata', 'loadeddata', 'play', 'playing', 'seeking', 'seeked', 'timeupdate', 'ended'].forEach(function (ev) {
      d.addEventListener(ev, function (e) { applySkip(e.target, e.type); }, true);
    });
    var origPlay = w.HTMLMediaElement && HTMLMediaElement.prototype.play;
    if (origPlay) {
      HTMLMediaElement.prototype.play = function () {
        var v = this;
        if (v.tagName === 'VIDEO' && !v.__pzSkipBound && !v.isConnected) {
          v.__pzSkipBound = true;
          ['loadstart', 'loadedmetadata', 'play', 'seeking', 'seeked', 'timeupdate', 'ended'].forEach(function (ev) { v.addEventListener(ev, function (e) { applySkip(v, e.type); }); });
        }
        applySkip(v);
        return origPlay.apply(v, arguments);
      };
    }
  }

  /* ---------- R8: theme videos never make sound ----------
     Every <video> is forced silent (muted property + defaultMuted / muted attribute + volume 0 + playsinline)
     when it is added to the page, starts loading, is played or changes volume. Only an element carrying
     data-pz-allow-sound is exempt (none do). Nets, because a video can be born in markup, cloned, built in JS or
     never attached (WebGL textures): the play() hook (runs before playback starts, attached or not), capture-phase
     media events on the document (media events don't bubble; loadstart fires on every src change / load()), and a
     sweep of the markup at start-up. No MutationObserver: it would run on every DOM change (CPU budget, R8). */
  function hush(v) {
    if (!v || v.tagName !== 'VIDEO' || (v.hasAttribute && v.hasAttribute('data-pz-allow-sound'))) return;
    try {
      if (!v.muted) v.muted = true;
      if (!v.defaultMuted) v.defaultMuted = true;
      if (v.volume !== 0) v.volume = 0;
      if (!v.playsInline) v.playsInline = true;
      if (!v.hasAttribute('playsinline')) v.setAttribute('playsinline', '');
    } catch (e) {}
  }
  PZ.hushVideo = hush;
  function hushAll(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.tagName === 'VIDEO') hush(root);
    Array.prototype.forEach.call(root.querySelectorAll('video'), hush);
  }
  ['loadstart', 'loadedmetadata', 'loadeddata', 'play', 'playing', 'volumechange'].forEach(function (ev) {
    d.addEventListener(ev, function (e) { hush(e.target); }, true);
  });
  var playLoud = w.HTMLMediaElement && HTMLMediaElement.prototype.play;
  if (playLoud) {
    HTMLMediaElement.prototype.play = function () {
      var v = this;
      hush(v);
      if (v.tagName === 'VIDEO' && !v.isConnected && !v.__pzHushBound) {
        v.__pzHushBound = true;
        ['loadstart', 'loadeddata', 'playing', 'volumechange'].forEach(function (ev) { v.addEventListener(ev, function () { hush(v); }); });
      }
      return playLoud.apply(v, arguments);
    };
  }
  hushAll(d);
  d.addEventListener('DOMContentLoaded', function () { hushAll(d); });
  d.addEventListener('pz:cards', function (e) { hushAll(e.target && e.target.querySelectorAll ? e.target : d); });

  /* ---------- R9 live phones: refused autoplay is retried on the next tap ----------
     An iPhone in Low Power Mode (and some data-saver setups) refuses even a muted inline play() with NotAllowedError,
     so product videos sat on their posters. Every play() the theme asks for that is refused that way is remembered
     and retried on the next user activation (touchend / pointerup / click / key, the events iOS accepts), for as long
     as the video is still wanted: its owner has not paused it since and it is on screen (or not in the DOM, e.g. a
     WebGL texture). PZ.autoplay: '' (nothing asked yet) | 'ok' | 'blocked' (last answer). */
  PZ.autoplay = '';
  var refused = [], retryArmed = false;
  var ACTIVATION = ['touchend', 'pointerup', 'click', 'keydown'];
  function armRetry(on) {
    if (retryArmed === on) return;
    retryArmed = on;
    ACTIVATION.forEach(function (ev) { (on ? w.addEventListener : w.removeEventListener).call(w, ev, retryRefused, { capture: true, passive: true }); });
  }
  function retryRefused() {
    var list = refused;
    refused = [];
    armRetry(false);
    list.forEach(function (v) {
      if (!v.__pzWant || !v.paused) return;
      if (v.isConnected) {
        var r = v.getBoundingClientRect();
        if (!r.width || r.bottom < -80 || r.top > w.innerHeight + 80) { refused.push(v); return; }
      }
      v.play();
    });
    if (refused.length) armRetry(true);
  }
  var playAsk = w.HTMLMediaElement && HTMLMediaElement.prototype.play;
  if (playAsk) {
    HTMLMediaElement.prototype.play = function () {
      var v = this;
      v.__pzWant = true;
      var p = playAsk.apply(v, arguments);
      if (p && p.then) p.then(function () { PZ.autoplay = 'ok'; }, function (err) {
        if (!err || err.name !== 'NotAllowedError') return;
        PZ.autoplay = 'blocked';
        if (refused.indexOf(v) < 0) refused.push(v);
        armRetry(true);
      });
      return p;
    };
    var pauseAsk = HTMLMediaElement.prototype.pause;
    HTMLMediaElement.prototype.pause = function () { this.__pzWant = false; return pauseAsk.apply(this, arguments); };
  }

  /* ---------- R9 live phones: GIFs animate near the screen ----------
     A GIF product image (1-2 MB spins; Shopify's CDN cannot resize GIFs) starts on phones as its still, from
     <source media="(max-width: 749px)" srcset|data-srcset="{still}" data-pz-gif="{gif}">. After the page's load
     event the source switches to the GIF while its image is on or within half a viewport of the screen, and back to
     the still when it leaves (an off-screen GIF is not decoded). Deferred sources (data-srcset, not hydrated yet) get
     the same swap on data-srcset, so hydration picks the right one. */
  var mqPhone = w.matchMedia('(max-width: 749px)');
  var gifIO = null, gifLive = false;
  function gifSet(src, on) {
    if (src.__pzGifOn === on) return;
    src.__pzGifOn = on;
    var url = on ? src.getAttribute('data-pz-gif') : src.__pzStill;
    if (!url) return;
    src.setAttribute(src.hasAttribute('data-srcset') ? 'data-srcset' : 'srcset', url);
  }
  function gifScan(root) {
    if (!gifLive || !mqPhone.matches || !('IntersectionObserver' in w)) return;
    if (!gifIO) gifIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { var s = e.target.__pzGifSrc; if (s) gifSet(s, e.isIntersecting); });
    }, { rootMargin: '50% 0px' });
    [].forEach.call((root && root.querySelectorAll ? root : d).querySelectorAll('source[data-pz-gif]'), function (s) {
      if (s.__pzGifBound) return;
      var img = s.parentNode && s.parentNode.querySelector('img');
      if (!img) return;
      s.__pzGifBound = true;
      s.__pzStill = s.getAttribute('srcset') || s.getAttribute('data-srcset') || '';
      s.__pzGifOn = false;
      img.__pzGifSrc = s;
      gifIO.observe(img);
    });
  }
  PZ.gifScan = gifScan;
  function gifStart() { gifLive = true; gifScan(d); }
  if (d.readyState === 'complete') setTimeout(gifStart, 0);
  else w.addEventListener('load', function () { setTimeout(gifStart, 0); });
  d.addEventListener('pz:cards', function (e) { gifScan(e.target); });
  d.addEventListener('shopify:section:load', function (e) { gifScan(e.target); });

  /* ---------- inView ---------- */
  PZ.inView = function (el, cb, opts) {
    opts = opts || {};
    if (!el) return function () {};
    var media = [];
    if (opts.media !== false) {
      if (el.tagName === 'VIDEO') media.push(el);
      media = media.concat(Array.prototype.slice.call(el.querySelectorAll('video[autoplay], video[data-pz-autoplay]')));
    }
    if (!('IntersectionObserver' in w)) { if (cb) cb(true, null); return function () {}; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var on = entry.isIntersecting;
        media.forEach(function (v) {
          if (on && !PZ.reduced) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
          else if (!on) v.pause();
        });
        if (cb) cb(on, entry);
        if (on && opts.once) io.disconnect();
      });
    }, { rootMargin: opts.rootMargin || '0px', threshold: opts.threshold != null ? opts.threshold : 0 });
    io.observe(el);
    return function () { io.disconnect(); };
  };

  /* ---------- helpers ---------- */
  PZ.money = function (cents) {
    var fmt = cfg.moneyFormat || '${{amount}}';
    var n = (Number(cents) || 0) / 100;
    function withDelims(num, dec, th, ds) {
      var parts = num.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return parts.join(ds);
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, k) {
      switch (k) {
        case 'amount_no_decimals': return withDelims(n, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(n, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelims(n, 2, "'", '.');
        default: return withDelims(n, 2, ',', '.');
      }
    });
  };
  PZ.pad = function (n, len) { n = String(n); while (n.length < (len || 2)) n = '0' + n; return n; };
  PZ.store = {
    get: function (k) { try { return w.localStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { w.localStorage.setItem(k, v); } catch (e) {} }
  };

  /* fit text to its container width: [data-pz-fit data-min="40" data-max="180"] */
  PZ.fit = function (el) {
    var max = parseFloat(el.getAttribute('data-max')) || 180;
    var min = parseFloat(el.getAttribute('data-min')) || 32;
    var parent = el.parentElement;
    if (!parent) return;
    /* R6 CLS: a heading pre-sized by CSS (data-fit-css: font-size = 100cqw / --fit-em, snippets/pz-fit-em) keeps that
       size while it fits. No measuring with fallback fonts, no size change after paint; only an overflow (a glyph the
       table does not know) falls through to the fit below, once the fonts are in. */
    if (el.hasAttribute('data-fit-css')) {
      if (d.fonts && d.fonts.status !== 'loaded') return;
      var pcs = getComputedStyle(parent);
      var av = parent.clientWidth - (parseFloat(pcs.paddingLeft) || 0) - (parseFloat(pcs.paddingRight) || 0);
      if (el.scrollWidth <= av + 1) { el.classList.add('is-fit'); return; }
    }
    /* launch gate: under prefers-reduced-motion the base CSS gives every element a .001ms transition, and WebKit then
       applies font-size changes a frame late, so the measure below read the old size (SHOP ALL overflowed iPhones with
       Reduce Motion on). Measure with transitions off. */
    el.style.transition = 'none';
    el.style.fontSize = max + 'px';
    el.style.whiteSpace = 'nowrap';
    var avail = parent.clientWidth - (parseFloat(getComputedStyle(parent).paddingLeft) || 0) - (parseFloat(getComputedStyle(parent).paddingRight) || 0);
    var sw = el.scrollWidth;
    if (sw > avail && sw > 0) {
      var size = Math.max(min, Math.floor(max * avail / sw) - 1);
      el.style.fontSize = size + 'px';
      if (size === min) el.style.whiteSpace = 'normal';
    }
    el.classList.add('is-fit');
  };
  function fitAll() { d.querySelectorAll('[data-pz-fit]').forEach(PZ.fit); }
  d.addEventListener('pz:ready', function () {
    fitAll();
    if (d.fonts && d.fonts.ready) d.fonts.ready.then(fitAll);
    /* launch gate: also re-fit once late fonts finish and on load */
    if (d.fonts && d.fonts.addEventListener) d.fonts.addEventListener('loadingdone', fitAll);
    w.addEventListener('load', fitAll);
  });
  var rt;
  w.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(fitAll, 120); });
  d.addEventListener('shopify:section:load', fitAll);

  /* ---------- ?pz_debug=1: on-device motion diagnostics ----------
     Read-only overlay for a phone screenshot: why motion might be off (PZ.reduced and its only input, the
     prefers-reduced-motion query), data saver, a muted-autoplay test, measured animation-frame rate (30 = iOS Low
     Power Mode), WebGL, the toybox state, awake sections, videos and GIFs on screen, script errors. Only with
     ?pz_debug=1 (kept for the tab; ?pz_debug=0 turns it off). */
  var dbg = false;
  try {
    if (/[?&]pz_debug=1/.test(w.location.search)) { dbg = true; w.sessionStorage.setItem('pz-debug', '1'); }
    else if (/[?&]pz_debug=0/.test(w.location.search)) w.sessionStorage.removeItem('pz-debug');
    else dbg = w.sessionStorage.getItem('pz-debug') === '1';
  } catch (e) { dbg = /[?&]pz_debug=1/.test(w.location.search); }
  if (dbg) {
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', debugPanel); else debugPanel();
  }
  function debugPanel() {
    var errs = w.__pzErr || (w.__pzErr = []);
    if (!w.__pzErrOn) {
      w.addEventListener('error', function (e) { if (errs.length < 15) errs.push((e.message || 'error') + (e.filename ? ' @' + e.filename.split('/').pop().split('?')[0] + ':' + e.lineno : '')); });
    }
    var box = d.createElement('div');
    box.id = 'pz-debug';
    box.setAttribute('style', 'position:fixed;left:6px;right:6px;bottom:6px;z-index:2147483647;max-height:58vh;overflow:auto;-webkit-overflow-scrolling:touch;background:rgba(0,0,0,.88);color:#7CFF9B;font:11px/1.35 ui-monospace,Menlo,monospace;padding:8px 8px 6px;border:1px solid #2BD567;border-radius:6px;white-space:pre-wrap;word-break:break-word;pointer-events:auto;');
    var bar = d.createElement('div');
    bar.setAttribute('style', 'display:flex;gap:8px;align-items:center;margin-bottom:4px;color:#fff;');
    bar.innerHTML = '<b style="flex:1">PZ DEBUG</b>';
    var tv = d.createElement('video');
    tv.setAttribute('style', 'width:48px;height:27px;background:#222;');
    tv.muted = true; tv.defaultMuted = true; tv.playsInline = true; tv.loop = true;
    tv.setAttribute('muted', ''); tv.setAttribute('playsinline', ''); tv.preload = 'auto';
    var btn = d.createElement('button');
    btn.type = 'button'; btn.textContent = 'hide';
    btn.setAttribute('style', 'font:inherit;color:#000;background:#7CFF9B;border:0;padding:2px 8px;border-radius:3px;');
    bar.appendChild(tv); bar.appendChild(btn);
    var out = d.createElement('div');
    box.appendChild(bar); box.appendChild(out);
    btn.addEventListener('click', function () { out.hidden = !out.hidden; btn.textContent = out.hidden ? 'show' : 'hide'; });
    d.body.appendChild(box);

    /* measured animation frames per second (rolling one-second windows) */
    var frames = 0, fps = [], fT = performance.now();
    (function tick(t) {
      frames++;
      if (t - fT >= 1000) { fps.push(Math.round(frames * 1000 / (t - fT))); if (fps.length > 8) fps.shift(); frames = 0; fT = t; }
      requestAnimationFrame(tick);
    })(fT);

    /* muted autoplay test with a video URL from this page */
    var ap = 'waiting for a video on the page', apT0 = 0;
    function apTest() {
      var src = '';
      [].some.call(d.querySelectorAll('video'), function (v) {
        if (v === tv) return false;
        var so = v.querySelector('source');
        src = v.currentSrc || v.getAttribute('src') || v.getAttribute('data-src') || (so && so.getAttribute('src')) || '';
        return !!src;
      });
      if (!src) { setTimeout(apTest, 1500); return; }
      ap = 'pending'; apT0 = performance.now();
      tv.src = src;
      var p;
      try { p = tv.play(); } catch (e) { ap = 'threw ' + e.name; return; }
      if (p && p.then) p.then(function () { ap = 'ALLOWED (' + Math.round(performance.now() - apT0) + ' ms)'; }, function (e) { ap = 'REFUSED ' + (e && e.name) + (e && e.name === 'NotAllowedError' ? ' (Low Power Mode / data saver?)' : '') + ' ' + ((e && e.message) || '').slice(0, 80); });
      else ap = 'no promise';
    }
    setTimeout(apTest, 1200);
    tv.addEventListener('playing', function () { if (/REFUSED/.test(ap) && ap.indexOf('tap') < 0) ap += ' -> plays after a tap'; });

    var gl = 'no';
    try {
      var cv = d.createElement('canvas');
      var ctx = cv.getContext('webgl2') || cv.getContext('webgl');
      if (ctx) {
        var ri = ctx.getExtension('WEBGL_debug_renderer_info');
        gl = 'ok ' + (w.WebGL2RenderingContext && ctx instanceof w.WebGL2RenderingContext ? 'webgl2' : 'webgl1') + (ri ? ' ' + String(ctx.getParameter(ri.UNMASKED_RENDERER_WEBGL)).slice(0, 40) : '');
        var lc = ctx.getExtension('WEBGL_lose_context'); if (lc) lc.loseContext();
      }
    } catch (e) { gl = 'error ' + e.name; }

    function yn(b) { return b ? 'YES' : 'no'; }
    function short(u) { return String(u || '').split('/').pop().split('?')[0].slice(0, 22); }
    function inView(el) { var r = el.getBoundingClientRect(); return r.width > 0 && r.bottom > 0 && r.top < w.innerHeight; }
    function render() {
      var nav = w.navigator, con = nav.connection || {};
      var mq = function (q) { return w.matchMedia(q).matches; };
      var ios = (nav.userAgent.match(/OS (\d+)_(\d+)(?:_(\d+))? like Mac/) || []).slice(1).filter(Boolean).join('.');
      var med = fps.slice().sort(function (a, b) { return a - b; })[fps.length >> 1] || 0;
      var vids = [].filter.call(d.querySelectorAll('video'), function (v) { return v !== tv && inView(v); });
      var playing = vids.filter(function (v) { return !v.paused && v.readyState > 2; });
      var stuck = vids.filter(function (v) { return v.paused || v.readyState < 3; }).slice(0, 4).map(function (v) {
        return short(v.currentSrc || v.getAttribute('src') || v.getAttribute('data-src')) + ' paused=' + v.paused + ' rs=' + v.readyState + ' ns=' + v.networkState + (v.error ? ' err=' + v.error.code : '') + ' want=' + !!v.__pzWant;
      });
      var gifs = [].slice.call(d.querySelectorAll('source[data-pz-gif]'));
      var tb = d.querySelector('[data-pz-section="toybox"]');
      var tbSim = tb && tb.__pzSim;
      var tbDbg = null;
      try { tbDbg = tbSim && tbSim.debug(); } catch (e) {}
      var secs = [].slice.call(d.querySelectorAll('[data-pz-section]'));
      var awake = secs.filter(function (s) { return !s.classList.contains('pz-off'); }).map(function (s) { return s.getAttribute('data-pz-section'); });
      var h = d.documentElement.classList;
      var lines = [
        'PZ.reduced ' + yn(PZ.reduced) + '  <- only input: prefers-reduced-motion = ' + (mq('(prefers-reduced-motion: reduce)') ? 'REDUCE (iOS Settings > Accessibility > Motion > Reduce Motion)' : 'no-preference'),
        'fps now ' + (fps[fps.length - 1] || '-') + ' / median ' + (med || '-') + (med && med <= 32 ? '  <- 30 fps cap: Low Power Mode?' : '') + '  (last ' + fps.join(',') + ')',
        'muted autoplay test: ' + ap + '   theme videos: ' + (PZ.autoplay || 'none asked yet'),
        'saveData ' + yn(con.saveData) + '  net ' + (con.effectiveType || '-') + '  reduced-data ' + yn(mq('(prefers-reduced-data: reduce)')) + '  contrast-more ' + yn(mq('(prefers-contrast: more)')),
        'WebGL ' + gl,
        'toybox ' + (tb ? (tb.className.replace(/\bpz-tb\b|\bshopify-section\b/g, '').replace(/\s+/g, ' ').trim() || '-') + (tb.getAttribute('data-tb-why') ? ' why=' + tb.getAttribute('data-tb-why') : '') + (tbDbg ? ' sim running=' + tbDbg.running + ' bodies=' + tbDbg.sprites.length : ' sim=none') + ' Matter=' + yn(w.Matter) : 'not on page'),
        'sections awake: ' + (awake.join(', ') || '-'),
        'videos in view ' + vids.length + ', playing ' + playing.length + (stuck.length ? '\n  stuck: ' + stuck.join('\n  stuck: ') : ''),
        'phone GIFs ' + gifs.filter(function (s) { return s.__pzGifOn; }).length + ' animating / ' + gifs.length + (mqPhone.matches ? '' : ' (desktop width: GIFs load directly)'),
        'idle ' + yn(PZ.idle) + '  boot ' + yn(h.contains('pz-boot-on')) + '  lenis ' + yn(PZ.lenis) + '  gsap ' + yn(w.gsap) + '  ST ' + yn(w.ScrollTrigger) + '  Lenis lib ' + yn(w.Lenis) + '  hidden ' + yn(d.hidden),
        'screen ' + w.innerWidth + 'x' + w.innerHeight + ' @' + (w.devicePixelRatio || 1) + 'x  touch ' + yn(PZ.touch) + '  cores ' + (nav.hardwareConcurrency || '-') + '  mem ' + (nav.deviceMemory || '-') + '  iOS ' + (ios || '-'),
        'errors (' + errs.length + '): ' + (errs.length ? '\n  ' + errs.slice(-6).join('\n  ') : 'none'),
        nav.userAgent
      ];
      out.textContent = lines.join('\n');
    }
    render();
    setInterval(render, 1000);
  }
})();
